import { createAsyncThunk, createSlice, AnyAction } from "@reduxjs/toolkit";
import BadRequestException from "./interfaces/BadRequestException";
import IResetPassword from "./interfaces/IResetPassword";
import User from "./interfaces/User";
import UserInputLogin from "./interfaces/UserInputLogin";
import UserInputRegistration from "./interfaces/UserInputRegistration";

const initialState: User = {
  id: -999,
  name: "",
  username: "",
  email: "",
  password: "",
  isError: false,
  isFetching: false,
  isRegistrationSuccess: false,
  isLoginSuccess: false,
  errorMessage: [],
  isAuth: false,
  isSuccess: false,
  isEmailVerified: false,
  successType: "",
  failureType: "",
};

export const signupUser = createAsyncThunk(
  "user/signupUser",
  async (
    { name, email, username, password, confirmPassword }: UserInputRegistration,
    thunkAPI
  ) => {
    try {
      if (password !== confirmPassword) {
        return thunkAPI.rejectWithValue({
          statusCode: 500,
          message: "Passwords do not match.",
        });
      }

      username = username.split(" ").join("_");

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/authentication/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
            username,
            name,
          }),
        }
      );
      if (response.status === 201) {
        return { name, email, username };
      } else {
        const { message, statusCode } =
          (await response.json()) as BadRequestException;
        return thunkAPI.rejectWithValue({ statusCode, message });
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async ({ username, password }: UserInputLogin, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/authentication/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            username,
            password,
          }),
        }
      );

      if (response.status === 200) {
        const { id, name, email, username } = await response.json();
        return { id, name, email, username };
      } else {
        const { statusCode, message } = await response.json();

        return thunkAPI.rejectWithValue({ statusCode, message });
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const authenticateUser = createAsyncThunk(
  "user/authenticateUser",
  async (_, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/authentication/`,
        {
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (response.status === 200) {
        // const responseJson = await response.json();
        const { email, username, id, name, isEmailConfirmed } =
          await response.json();

        return { id, username, email, name, isEmailConfirmed };
      }

      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const logoutUser = createAsyncThunk(
  "user/logoutUser",
  async (_, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/authentication/logout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (response.status === 200) {
        return { status: "OK" };
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const deleteUserAccount = createAsyncThunk(
  "user/deleteAccount",
  async (id: number, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/authentication/${id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (response.status === 200) {
        return { status: "OK" };
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  "user/resetPassword",
  async (
    { id, password, newPassword, confirmNewPassword }: IResetPassword,
    thunkAPI
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/authentication/${id}`,
        // `${process.env.REACT_APP_BACKEND_URL}/authentication/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            password,
            newPassword,
            confirmNewPassword,
          }),
        }
      );

      if (response.status === 200) {
        return { status: "OK" };
      }

      const { statusCode, message } = await response.json();

      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const resendVerificationLink = createAsyncThunk(
  "user/resendVerificationLink",
  async (_, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/email-confirmation/resend-confirmation-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.errorMessage = [];
      state.errorMessage.length = 0;
      state.isRegistrationSuccess = false;
      state.isLoginSuccess = false;
      state.isSuccess = false;

      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signupUser.fulfilled, (state, { payload }: any) => {
      state.email = payload.email;
      state.username = payload.username;
      state.name = payload.name;
      state.isRegistrationSuccess = true;

      return state;
    });
    builder.addCase(signupUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(signupUser.rejected, (state, action: AnyAction) => {
      state.isFetching = false;
      state.isError = true;

      if (action?.payload?.message === "Unauthorized") {
        return state;
      } else {
        state.errorMessage.push(action?.payload?.message);
        return state;
      }
    });

    builder.addCase(loginUser.fulfilled, (state, { payload }: any) => {
      state.email = payload.email;
      state.username = payload.username;
      state.name = payload.name;
      state.id = payload.id;
      state.isLoginSuccess = true;
      return state;
    });
    builder.addCase(loginUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(loginUser.rejected, (state, action: AnyAction) => {
      state.isFetching = false;
      state.isError = true;
      state.isLoginSuccess = false;
      // state.errorMessage.push(action?.payload?.message);
      if (action?.payload?.message === "Unauthorized") {
        return state;
      } else {
        state.errorMessage.push(action?.payload?.message);
        return state;
      }
    });

    builder.addCase(authenticateUser.fulfilled, (state, { payload }: any) => {
      state.isError = false;
      state.isAuth = true;
      state.errorMessage = [];
      state.email = payload?.email;
      state.username = payload?.username;
      state.id = payload?.id;
      state.name = payload?.name;
      state.isEmailVerified = payload?.isEmailConfirmed;
    });
    builder.addCase(authenticateUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(authenticateUser.rejected, (state, action: AnyAction) => {
      state.isFetching = false;
      state.isError = true;
      state.isAuth = false;

      if (action?.payload?.message === "Unauthorized") {
        return state;
      } else {
        state.errorMessage.push(action?.payload?.message);
        return state;
      }
    });

    builder.addCase(logoutUser.fulfilled, (state, { payload }) => {
      state.id = -999;
      state.name = "";
      state.username = "";
      state.email = "";
      state.password = "";
      state.isError = false;
      state.isFetching = false;
      state.isRegistrationSuccess = false;
      state.isLoginSuccess = false;
      state.errorMessage = [];
      state.isAuth = false;

      return state;
    });
    builder.addCase(logoutUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(logoutUser.rejected, (state, action: AnyAction) => {
      state.isFetching = false;
      state.isError = true;
      // state.errorMessage.push(action?.payload?.message);

      if (action?.payload?.message === "Unauthorized") {
        return state;
      } else {
        state.errorMessage.push(action?.payload?.message);
        return state;
      }
    });

    builder.addCase(deleteUserAccount.fulfilled, (state, { payload }) => {
      state.id = -999;
      state.name = "";
      state.username = "";
      state.email = "";
      state.password = "";
      state.isError = false;
      state.isFetching = false;
      state.isRegistrationSuccess = false;
      state.isLoginSuccess = false;
      state.errorMessage = [];
      state.isAuth = false;

      return state;
    });
    builder.addCase(deleteUserAccount.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(deleteUserAccount.rejected, (state, action: AnyAction) => {
      state.isFetching = false;
      state.isError = true;
      // state.errorMessage.push(action?.payload?.message);

      if (action?.payload?.message === "Unauthorized") {
        return state;
      } else {
        state.errorMessage.push(action?.payload?.message);
        return state;
      }
    });

    builder.addCase(resetUserPassword.fulfilled, (state, { payload }) => {
      state.errorMessage = [];
      state.isError = false;
      state.isFetching = false;
      state.isSuccess = true;
      state.successType = "resetUserPassword";

      return state;
    });
    builder.addCase(resetUserPassword.pending, (state) => {
      state.isFetching = true;
      state.isSuccess = false;

      return state;
    });
    builder.addCase(resetUserPassword.rejected, (state, action: AnyAction) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage.push(action?.payload?.message);
      state.isSuccess = false;
      state.failureType = "resetUserPassword";

      return state;
    });

    builder.addCase(resendVerificationLink.fulfilled, (state, { payload }) => {
      state.errorMessage = [];
      state.isError = false;
      state.isFetching = false;
      state.isSuccess = true;
      state.successType = "resendVerificationLink";

      return state;
    });
    builder.addCase(resendVerificationLink.pending, (state) => {
      state.isFetching = true;
      state.isSuccess = false;

      return state;
    });
    builder.addCase(
      resendVerificationLink.rejected,
      (state, action: AnyAction) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage.push(action?.payload?.message);
        state.isSuccess = false;
        state.failureType = "resendVerificationLink";

        return state;
      }
    );
  },
});

export const { clearState } = userSlice.actions;
export default userSlice.reducer;
